import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MessageCircle, Plus, Clock, CheckCircle, XCircle, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

const priorityColor = (p) => {
  if (p === 'high' || p === 'urgent') return 'bg-red-100 text-red-700';
  if (p === 'medium') return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-600';
};
const statusColor = (s) => {
  if (s === 'resolved' || s === 'closed') return 'bg-emerald-100 text-emerald-700';
  if (s === 'in_progress') return 'bg-blue-100 text-blue-700';
  return 'bg-amber-100 text-amber-700';
};
const statusIcon = (s) => {
  if (s === 'resolved' || s === 'closed') return <CheckCircle size={13} className="text-emerald-500" />;
  return <Clock size={13} className="text-amber-500" />;
};

export default function CustomerSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form, setForm] = useState({ subject: '', message: '', priority: 'low' });
  const [replyMsg, setReplyMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replying, setReplying] = useState(false);

  const fetchData = () => {
    api.get('/customer/support')
      .then(r => setTickets(r.data.tickets || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!form.subject.trim() || !form.message.trim()) { toast.error('Lengkapi subjek dan pesan'); return; }
    setSubmitting(true);
    try {
      await api.post('/customer/support', form);
      toast.success('Tiket berhasil dibuat, tim kami akan segera merespons');
      setCreateDialog(false);
      setForm({ subject: '', message: '', priority: 'low' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal membuat tiket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyMsg.trim()) { toast.error('Pesan tidak boleh kosong'); return; }
    setReplying(true);
    try {
      await api.post(`/customer/support/${selectedTicket.id}/reply`, { message: replyMsg });
      toast.success('Balasan terkirim');
      setReplyMsg('');
      // Refresh ticket detail
      const res = await api.get(`/customer/support/${selectedTicket.id}`);
      setSelectedTicket(res.data.ticket);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mengirim balasan');
    } finally {
      setReplying(false);
    }
  };

  const openTicket = async (ticket) => {
    try {
      const res = await api.get(`/customer/support/${ticket.id}`);
      setSelectedTicket(res.data.ticket);
    } catch {
      setSelectedTicket(ticket);
    }
    setViewDialog(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" />
    </div>
  );

  return (
    <div data-testid="customer-support-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Bantuan & Support</h1>
          <p className="text-sm text-slate-500 mt-1">Hubungi tim kami jika ada pertanyaan atau kendala</p>
        </div>
        <Button onClick={() => setCreateDialog(true)} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
          <Plus size={16} className="mr-2" /> Buat Tiket
        </Button>
      </div>

      {tickets.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <MessageCircle size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="font-medium text-slate-900 mb-1">Belum ada tiket support</h3>
            <p className="text-sm text-slate-500 mb-4">Jika ada pertanyaan atau kendala, buat tiket support dan tim kami akan merespons</p>
            <Button onClick={() => setCreateDialog(true)} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
              <Plus size={16} className="mr-2" /> Buat Tiket Baru
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map(ticket => (
            <Card key={ticket.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => openTicket(ticket)}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                      <MessageCircle size={20} className="text-[var(--store-primary, #2D6CDF)]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{ticket.subject}</p>
                      <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{ticket.message}</p>
                      <p className="text-xs text-slate-400 mt-1">{new Date(ticket.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Badge className={statusColor(ticket.status)}>
                      <span className="flex items-center gap-1">{statusIcon(ticket.status)}{ticket.status}</span>
                    </Badge>
                    <Badge variant="outline" className={`text-xs ${priorityColor(ticket.priority)}`}>{ticket.priority}</Badge>
                    {ticket.replies_count > 0 && (
                      <p className="text-xs text-slate-400">{ticket.replies_count} balasan</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Ticket Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Buat Tiket Support</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Subjek <span className="text-red-500">*</span></Label>
              <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Ringkasan masalah Anda" className="mt-1" />
            </div>
            <div>
              <Label>Prioritas</Label>
              <select
                className="w-full mt-1 h-9 px-3 rounded-md border border-input bg-background text-sm"
                value={form.priority}
                onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
              >
                <option value="low">Low - Pertanyaan umum</option>
                <option value="medium">Medium - Perlu bantuan segera</option>
                <option value="high">High - Masalah mendesak</option>
              </select>
            </div>
            <div>
              <Label>Detail Pesan <span className="text-red-500">*</span></Label>
              <Textarea
                value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                placeholder="Jelaskan masalah atau pertanyaan Anda secara detail..."
                rows={5}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Batal</Button>
            <Button onClick={handleCreate} disabled={submitting} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
              {submitting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Mengirim...</> : <><Send size={16} className="mr-2" /> Kirim Tiket</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Ticket Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>{selectedTicket?.subject}</span>
              {selectedTicket && <Badge className={statusColor(selectedTicket.status)}>{selectedTicket.status}</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 py-2">
            {/* Original message */}
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-xs text-slate-400 mb-1">Anda · {selectedTicket && new Date(selectedTicket.created_at).toLocaleDateString('id-ID')}</p>
              <p className="text-sm text-slate-700">{selectedTicket?.message}</p>
            </div>
            {/* Replies */}
            {(selectedTicket?.replies || []).map((reply, i) => (
              <div key={i} className={`rounded-lg p-4 ${reply.role === 'support' ? 'bg-emerald-50 ml-4' : 'bg-slate-50'}`}>
                <p className="text-xs text-slate-400 mb-1">
                  {reply.role === 'support' ? '🛟 Tim Support' : 'Anda'} · {new Date(reply.created_at).toLocaleDateString('id-ID')}
                </p>
                <p className="text-sm text-slate-700">{reply.message}</p>
              </div>
            ))}
          </div>
          {selectedTicket?.status !== 'closed' && selectedTicket?.status !== 'resolved' && (
            <div className="border-t pt-4 mt-2">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Tulis balasan..."
                  value={replyMsg}
                  onChange={e => setReplyMsg(e.target.value)}
                  rows={2}
                  className="flex-1 resize-none"
                />
                <Button onClick={handleReply} disabled={replying} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)] self-end">
                  {replying ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
